<template>
    <ClientGetDiv>
        <ClientGetTitleArea>
            <AccountSupervisor :size="35" :style="{ paddingTop: '4px' }" />
            <TitleText>수급자 목록</TitleText>
        </ClientGetTitleArea>

        <SearchAndStatsContainer>
            <ClientTypeSelect v-model="selectedClientType" @change="onClientTypeChange">
                <option value="all">전체</option>
                <option value="recipients">급여</option>
                <option value="b_recipients">비급여</option>
            </ClientTypeSelect>

            <ClientGetSearchArea>
                <ClientGetSearchInputTxt v-model="searchQuery" type="text" placeholder="수급자명, 인정번호를 입력하세요"
                    @keyup.enter="fetchData" />
                <ClientGetSearchBtn @click="fetchData">
                    <Magnify />
                </ClientGetSearchBtn>
            </ClientGetSearchArea>

            <StatsBox>
                <StatItem>
                    <StatLabel>조회된 수급자</StatLabel>
                    <StatValue>{{ clients_info.total_all_cnt }}</StatValue>명
                </StatItem>
            </StatsBox>
        </SearchAndStatsContainer>

        <ClientGetTableContainer>
            <!-- 로딩 스피너 -->
            <LoadingOverlay v-if="isLoadingClients">
                <LoadingSpinner></LoadingSpinner>
                <LoadingText>수급자 목록을 불러오는 중입니다...</LoadingText>
            </LoadingOverlay>

            <ClientGetTable v-if="!isLoadingClients">
                <ClientGetTableThead>
                    <ClientGetTableTr>
                        <th>수급자성함</th>
                        <th>인정번호</th>
                        <th>기관기호</th>
                        <th>대상</th>
                        <th class="address">주소</th>
                        <th>보호자성함</th>
                        <th>관계</th>
                        <th>담당자</th>
                    </ClientGetTableTr>
                </ClientGetTableThead>
                <ClientGetTableTbody>
                    <ClientGetTableTr v-for="(client, index) in pagedClients" :key="index" :class="{
                        'success': client.target === '일반',
                        'danger': client.target === '기초',
                        'info': client.target === '의료급여',
                        'warning': client.target === '감경',
                        'client_tr': true,
                    }" @click="clientDetail(client); clientModalClose()">
                        <td>{{ client.name }}</td>
                        <td>{{ client.number }}</td>
                        <td>{{ client.cnum }}</td>
                        <td>
                            <TargetBadge :target="client.target">
                                {{ client.target }}
                            </TargetBadge>
                        </td>
                        <td class="address">{{ client.address }}</td>
                        <td>{{ client.guardname }}</td>
                        <td>{{ client.guardtarget }}</td>
                        <td>{{ client.manager }}</td>
                    </ClientGetTableTr>
                </ClientGetTableTbody>
            </ClientGetTable>
        </ClientGetTableContainer>

        <!-- !페이지네이션 -->
        <PaginationContainer v-if="totalPages > 1 && !isLoadingClients">
            <Pagination>
                <PaginationButton @click="changePage(1)" :disabled="currentPage === 1">
                    <ChevronDoubleLeft :size="20" :style="{ paddingTop: '2px' }" />
                </PaginationButton>
                <PaginationButton @click="changePage(currentPage - 1)" :disabled="currentPage === 1">
                    <ChevronLeft :size="20" :style="{ paddingTop: '2px' }" />
                </PaginationButton>

                <PaginationNumber v-for="page in visiblePages" :key="page" :active="page === currentPage"
                    @click="changePage(page)">
                    {{ page }}
                </PaginationNumber>

                <PaginationButton @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">
                    <ChevronRight :size="20" :style="{ paddingTop: '2px' }" />
                </PaginationButton>
                <PaginationButton @click="changePage(totalPages)" :disabled="currentPage === totalPages">
                    <ChevronDoubleRight :size="20" :style="{ paddingTop: '2px' }" />
                </PaginationButton>
            </Pagination>
        </PaginationContainer>
    </ClientGetDiv>
</template>

<script>
import * as injectGlobal from '@/assets/styles/global/public.js';
import * as ClientGetCss from '@/assets/styles/order/modal/ClientGetModalCss.js';

import AccountSupervisor from 'vue-material-design-icons/AccountSupervisor.vue';
import Magnify from 'vue-material-design-icons/Magnify.vue';
import Plus from 'vue-material-design-icons/Plus.vue';
import ChevronDoubleLeft from 'vue-material-design-icons/ChevronDoubleLeft.vue';
import ChevronDoubleRight from 'vue-material-design-icons/ChevronDoubleRight.vue';
import ChevronLeft from 'vue-material-design-icons/ChevronLeft.vue';
import ChevronRight from 'vue-material-design-icons/ChevronRight.vue';

export default {
    components: {
        ...injectGlobal,
        ...ClientGetCss,
        AccountSupervisor,
        Magnify,
        Plus,
        ChevronDoubleLeft,
        ChevronDoubleRight,
        ChevronLeft,
        ChevronRight,
    },

    emits: ['clientDetail', 'close'],

    data() {
        return {
            clients_info: {
                clients: [],
                total_all_cnt: 0,
            },
            searchQuery: '',
            currentPage: 1,
            perPage: 10,
            totalPages: 1,
            selectedClientType: 'all',
            isLoadingClients: false,
        };
    },

    computed: {
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },
        pagedClients() {
            return this.clients_info.clients;
        },
        visiblePages() {
            const pages = [];
            const maxVisiblePages = 10; // 최대 10개 페이지 표시

            // 현재 페이지가 10 이하인 경우
            if (this.totalPages <= maxVisiblePages) {
                // 전체 페이지를 모두 표시
                for (let i = 1; i <= this.totalPages; i++) {
                    pages.push(i);
                }
            } else {
                // 현재 페이지가 10 이하인 경우: 1~10 표시
                if (this.currentPage <= 10) {
                    for (let i = 1; i <= 10; i++) {
                        pages.push(i);
                    }
                }
                // 현재 페이지가 10을 넘은 경우: 10개씩 그룹으로 이동
                else {
                    const groupStart = Math.floor((this.currentPage - 1) / 10) * 10 + 1;
                    const groupEnd = Math.min(groupStart + 9, this.totalPages);

                    for (let i = groupStart; i <= groupEnd; i++) {
                        pages.push(i);
                    }
                }
            }

            return pages;
        },
    },

    methods: {
        async fetchAllStats() {
            try {
                const apiUrl = `${this.$apiUrls.hanqmedi}/getClients/${this.$globalState.company_key}`;

                // 전체 통계를 가져오기 위해 필터 없이 조회
                const response = await this.$axios({
                    method: 'get',
                    url: apiUrl,
                    params: {
                        page: 1,
                        limit: 1, // 통계만 필요하므로 최소한의 데이터만 가져오기
                        search: '', // 검색 필터 없이
                        type: '', // 타입 필터 없이
                        target: '' // 대상 필터 없이
                    },
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                // total_count만 업데이트 (전체 수급자 수)
                if (response.data && response.data.total_count !== undefined) {
                    this.clients_info.total_all_cnt = response.data.total_count || 0;
                }
            } catch (error) {
                console.error('전체 통계를 가져오는 중 오류 발생:', error);
                // 통계 로드 실패해도 계속 진행
            }
        },

        // 급여/비급여 선택 변경 시 호출되는 메서드
        onClientTypeChange() {
            this.currentPage = 1; // 페이지 초기화
            this.searchQuery = ''; // 검색어 초기화
            this.fetchData();
        },

        async fetchData() {
            this.isLoadingClients = true;

            try {
                if (!this.$globalState.company_key) {
                    const userInfo = localStorage.getItem('userInfo');
                    if (userInfo) {
                        const parsedInfo = JSON.parse(userInfo);
                        this.$setGlobalState('id', parsedInfo.id);
                        this.$setGlobalState('manager_name', parsedInfo.name);
                        this.$setGlobalState('company_name', parsedInfo.company);
                        this.$setGlobalState('company_key', parsedInfo.company_key);
                    } else {
                        this.$swal.fire('인증 오류', '로그인이 필요합니다.', 'error');
                        this.$router.push('/login');
                        return;
                    }
                }

                // 백엔드 API를 통해 수급자 정보 조회 (절대 경로 사용)
                const apiUrl = `${this.$apiUrls.hanqmedi}/getClients/${this.$globalState.company_key}`;

                // 선택된 수급자 타입에 따른 파라미터 설정
                let typeParam = '';
                if (this.selectedClientType === 'recipients') {
                    typeParam = '급여';
                } else if (this.selectedClientType === 'b_recipients') {
                    typeParam = '비급여';
                }
                // 'all'인 경우 typeParam은 빈 문자열로 유지

                const response = await this.$axios({
                    method: 'get',
                    url: apiUrl,
                    params: {
                        limit: this.perPage,
                        page: this.currentPage,
                        search: this.searchQuery,
                        type: typeParam
                    },
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                // API 응답 구조에 맞게 수정
                if (response.data && response.data.recipients && Array.isArray(response.data.recipients)) {
                    // API 응답 데이터를 프론트엔드에서 사용하는 형태로 매핑
                    this.clients_info.clients = response.data.recipients.map(client => ({
                        name: client.name || '', // 수급자성함
                        number: client.number || '', // 인정번호
                        cnum: client.cnum || '', // 기관기호
                        target: client.target || '', // 대상
                        address: client.address || '', // 주소
                        guardname: client.guard_name || '', // 보호자성함
                        guardtarget: client.guard_target || '', // 관계
                        manager: client.manager || '', // 담당자
                        phone1: client.phone1 || '', // 전화번호1
                        phone2: client.phone2 || '', // 전화번호2
                        // 추가 필드들도 매핑
                        id: client.id,
                        company_id: client.company_id, // 회사ID
                        user_id: client.user_id, // 사용자ID
                        address2: client.address2 || '', // 주소2
                        address_detail: client.address_detail || '', // 주소상세
                        address_num: client.address_num || '', // 주소번호
                        bank_accout: client.bank_accout || '', // 계좌번호
                        bank_admin: client.bank_admin || '', // 계좌주
                        bank_name: client.bank_name || '', // 은행명
                        bank_phone: client.bank_phone || '', // 은행전화번호
                        bank_resident: client.bank_resident || '', // 은행주소
                        center_name: client.center_name || '', // 센터명
                        cnum: client.cnum || '', // 센터번호
                        gender: client.gender || '', // 성별
                        guard_resident: client.guard_resident || '', // 보호자생년월일
                        memo: client.memo || '', // 메모
                        msgno: client.msgno || '', // 메시지번호
                        ranker: client.ranker || '', // 순위
                        rcgt: client.rcgt || '', // 주소
                        sale: client.sale || 0, // 판매
                        // 수급자 타입 정보 추가
                        type: client.type || '급여',
                        source: client.source || 'unknown',
                        isNonBenefit: client.isNonBenefit || false
                    }));

                    // 전체 페이지 수 계산 (data property로 직접 수정)
                    this.totalPages = response.data.total_page || 1;

                    if (this.searchQuery && this.clients_info.clients.length === 0) {
                        this.$swal.fire('검색 결과 없음', `${this.searchQuery} 검색 결과가 없습니다.`, 'info');
                    }
                } else {
                    this.clients_info.clients = [];
                    this.totalPages = 1;
                    if (this.searchQuery) {
                        this.$swal.fire('검색 결과 없음', `${this.searchQuery} 검색 결과가 없습니다.`, 'info');
                    }
                }
            } catch (error) {
                console.error('데이터를 가져오는 중 오류 발생:', error);
                this.$swal.fire('오류', '데이터를 불러오는데 실패했습니다.', 'error');
                this.clients_info.clients = [];
                this.totalPages = 1;
            } finally {
                this.isLoadingClients = false;
            }
        },

        changePage(page) {
            if (page !== this.currentPage && page > 0 && page <= this.totalPages) {
                this.currentPage = page;
                this.$nextTick(() => {
                    this.fetchData();
                });
            }
        },

        clientDetail(client) {
            const modifiedClient = { ...client };
            if (modifiedClient.phone1 === modifiedClient.phone2) {
                modifiedClient.phone2 = '';
            }
            this.$emit('clientDetail', modifiedClient);
            this.$swal.fire({
                title: '수급자 선택 완료',
                text: '수급자 정보가 성공적으로 선택되었습니다.',
                icon: 'success',
                showConfirmButton: false,
                timer: 1200
            });
        },

        clientModalClose() {
            this.$emit('close');
        },

        initializeAddressSearch() {
            const addressSearchBtn = document.getElementById('addressSearchBtn');
            if (addressSearchBtn) {
                addressSearchBtn.addEventListener('click', () => {
                    this.searchAddress();
                });
            }
        },

        searchAddress() {
            new window.daum.Postcode({
                oncomplete: (data) => {
                    document.getElementById('address').value = data.address;
                    document.getElementById('address_detail').focus();
                }
            }).open();
        },

        handleEscKey(event) {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                this.$emit('close');
            }
        }
    },

    created() {
        document.addEventListener('keydown', this.handleEscKey);
    },

    beforeUnmount() {
        document.removeEventListener('keydown', this.handleEscKey);
    },

    mounted() {
        this.fetchAllStats();
        this.fetchData();
    },

    watch: {
        totalPages(newVal) {
            if (this.currentPage > newVal) {
                this.currentPage = Math.max(1, newVal);
            }
        },
    },
};
</script>

<style scoped>
/* 블러 효과를 위한 추가 스타일 */
.blurred {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}

/* 블러된 요소 내부의 상호작용은 유지 (클릭 가능) */
.blurred * {
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}

/* 테이블 셀 내용 블러 처리 */
.blurred td {
    filter: blur(4px) !important;
}
</style>